import React, { useState, useEffect } from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { Input } from '@vehix/portal/components/ui/input';
import { Label } from '@vehix/portal/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vehix/portal/components/ui/tabs';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Clock, Calendar, AlertCircle } from 'lucide-react';
import { CRON_PRESETS, describeCron, validateCron } from '@vehix/portal/services/api/scheduling';
import { cn } from '@vehix/portal/lib/utils';

interface CronBuilderProps {
  value: string;
  onChange: (cron: string) => void;
  error?: string;
}

const WEEKDAYS = [
  { value: '0', label: 'Sun' },
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' }
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`
}));

const MINUTES = [
  { value: '0', label: ':00' },
  { value: '15', label: ':15' },
  { value: '30', label: ':30' },
  { value: '45', label: ':45' }
];

export const CronBuilder: React.FC<CronBuilderProps> = ({ value, onChange, error }) => {
  const [mode, setMode] = useState<'preset' | 'custom'>('preset');
  const [customCron, setCustomCron] = useState(value || '');
  const [customError, setCustomError] = useState<string | undefined>();

  // Custom builder state
  const [hour, setHour] = useState('6');
  const [minute, setMinute] = useState('0');
  const [selectedDays, setSelectedDays] = useState<string[]>(['1']); // Monday default
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');

  // Parse initial value to set builder state
  useEffect(() => {
    if (!value) return;

    // Check if it's a preset
    const isPreset = CRON_PRESETS.some(p => p.cron === value);
    if (isPreset) {
      setMode('preset');
      return;
    }

    // Try to parse for the builder
    const parts = value.split(' ');
    if (parts.length === 5) {
      const [min, hr, , , dow] = parts;
      if (min !== '*' && hr !== '*') {
        setMinute(min);
        setHour(hr);
      }
      if (dow !== '*') {
        if (dow.includes(',')) {
          setSelectedDays(dow.split(','));
          setFrequency('weekly');
        } else if (dow.includes('-')) {
          const [start, end] = dow.split('-');
          const days = [];
          for (let i = parseInt(start); i <= parseInt(end); i++) {
            days.push(String(i));
          }
          setSelectedDays(days);
          setFrequency('weekly');
        } else {
          setSelectedDays([dow]);
          setFrequency('weekly');
        }
      } else {
        setFrequency('daily');
      }
    }

    setCustomCron(value);
  }, [value]);

  // Build cron from builder state
  const buildCron = (): string => {
    if (frequency === 'daily') {
      return `${minute} ${hour} * * *`;
    } else if (frequency === 'weekly') {
      const days = selectedDays.length > 0 ? selectedDays.sort().join(',') : '*';
      return `${minute} ${hour} * * ${days}`;
    } else if (frequency === 'monthly') {
      return `${minute} ${hour} 1 * *`;
    }
    return `${minute} ${hour} * * *`;
  };

  // Handle preset selection
  const handlePresetSelect = (cron: string) => {
    onChange(cron);
    setCustomCron(cron);
  };

  // Handle custom cron input
  const handleCustomCronChange = (newCron: string) => {
    setCustomCron(newCron);
    const validation = validateCron(newCron);
    if (validation.valid) {
      setCustomError(undefined);
      onChange(newCron);
    } else {
      setCustomError(validation.error);
    }
  };

  // Handle builder changes
  const handleBuilderChange = () => {
    const cron = buildCron();
    onChange(cron);
    setCustomCron(cron);
  };

  // Update cron when builder values change
  useEffect(() => {
    if (mode === 'custom') {
      handleBuilderChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hour, minute, selectedDays, frequency]);

  // Toggle day selection
  const toggleDay = (day: string) => {
    setSelectedDays(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      }
      return [...prev, day];
    });
  };

  return (
    <div className='space-y-4'>
      <Tabs value={mode} onValueChange={v => setMode(v as 'preset' | 'custom')}>
        <TabsList className='grid w-full grid-cols-2'>
          <TabsTrigger value='preset' className='flex items-center gap-2'>
            <Clock className='h-4 w-4' />
            Quick Presets
          </TabsTrigger>
          <TabsTrigger value='custom' className='flex items-center gap-2'>
            <Calendar className='h-4 w-4' />
            Custom Schedule
          </TabsTrigger>
        </TabsList>

        <TabsContent value='preset' className='space-y-3 mt-4'>
          <div className='grid grid-cols-1 gap-2'>
            {CRON_PRESETS.map(preset => (
              <Button
                key={preset.cron}
                type='button'
                variant={value === preset.cron ? 'default' : 'outline'}
                className={cn(
                  'justify-start h-auto py-3 px-4',
                  value === preset.cron && 'ring-2 ring-primary ring-offset-2'
                )}
                onClick={() => handlePresetSelect(preset.cron)}
              >
                <div className='flex flex-col items-start'>
                  <span className='font-medium'>{preset.label}</span>
                  <span className='text-xs text-muted-foreground'>{preset.description}</span>
                </div>
              </Button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value='custom' className='space-y-4 mt-4'>
          {/* Frequency Selection */}
          <div className='space-y-2'>
            <Label>Frequency</Label>
            <Select
              value={frequency}
              onValueChange={(v: 'daily' | 'weekly' | 'monthly') => setFrequency(v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='daily'>Daily</SelectItem>
                <SelectItem value='weekly'>Weekly</SelectItem>
                <SelectItem value='monthly'>Monthly (1st of month)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Time Selection */}
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label>Hour</Label>
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map(h => (
                    <SelectItem key={h.value} value={h.value}>
                      {h.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label>Minute</Label>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MINUTES.map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Day Selection (for weekly) */}
          {frequency === 'weekly' && (
            <div className='space-y-2'>
              <Label>Days of the Week</Label>
              <div className='flex flex-wrap gap-2'>
                {WEEKDAYS.map(day => (
                  <Button
                    key={day.value}
                    type='button'
                    variant={selectedDays.includes(day.value) ? 'default' : 'outline'}
                    size='sm'
                    className='w-12'
                    onClick={() => toggleDay(day.value)}
                  >
                    {day.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Advanced: Raw Cron Input */}
          <div className='space-y-2 pt-4 border-t'>
            <Label className='text-sm text-muted-foreground'>Advanced: Raw Cron Expression</Label>
            <Input
              value={customCron}
              onChange={e => handleCustomCronChange(e.target.value)}
              placeholder='0 6 * * 1'
              className={cn(customError && 'border-destructive')}
            />
            {customError && (
              <p className='text-xs text-destructive flex items-center gap-1'>
                <AlertCircle className='h-3 w-3' />
                {customError}
              </p>
            )}
            <p className='text-xs text-muted-foreground'>
              Format: minute hour day-of-month month day-of-week
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Current Schedule Display */}
      {value && (
        <div className='flex items-center gap-2 p-3 bg-muted/50 rounded-lg'>
          <Clock className='h-4 w-4 text-muted-foreground' />
          <div className='flex-1'>
            <span className='text-sm font-medium'>Current Schedule: </span>
            <span className='text-sm text-muted-foreground'>{describeCron(value)}</span>
          </div>
          <Badge variant='outline' className='font-mono text-xs'>
            {value}
          </Badge>
        </div>
      )}

      {error && (
        <p className='text-sm text-destructive flex items-center gap-1'>
          <AlertCircle className='h-4 w-4' />
          {error}
        </p>
      )}
    </div>
  );
};

export default CronBuilder;
